/* eslint-disable react/jsx-props-no-spreading */
import React, { ReactElement, useEffect } from 'react'
import { Route, Switch } from 'react-router-dom'

import { BaseSession } from '@ally/federated-types'
import whichEnv from '@ally/which-env'
import { MaxAppWidth } from '@ally/next-layout'

import { AllyLoginProvider } from './AllyLoginProvider'
import { AllyLoginRouter } from './AllyLoginRouter'
import { useSession } from './SessionContext'
import { useHostServices } from './HostServices'
import { FeatureFlag } from './Utils'
import log from './whisper'

interface LoginProps {
  className?: string
}

interface LoginFormProps {
  setSession: (session: BaseSession) => void
}

/**
 * Displays the @ally/ally-login <LoginForm /> and allows the user to
 * authenticate. Once authenticated, the user is redirected to the dashboard.
 */
export const LoginForm: React.FC<LoginFormProps> = ({ setSession }) => {
  const getCookieValue = (name: string): string => {
    return (
      document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`)?.pop() || ''
    )
  }

  const { logout, sessionStatus } = useSession()
  const { utils, featureFlags, session } = useHostServices()

  const isAccountsPrehydrationEnabled = featureFlags.variation(
    'FF_accounts-prehydration',
    false,
  )

  const apiKey = whichEnv(window.location.href).pick({
    dev: 'wU5VdAI4fv7jgsaAOJDREixc3MOaUs1R',
    cap: 'BdWWhZRHCmdNq74CJYfaozM2gZCZqEDs',
    fix: 'BdWWhZRHCmdNq74CJYfaozM2gZCZqEDs',
    qa1: 'BdWWhZRHCmdNq74CJYfaozM2gZCZqEDs',
    qa2: 'BdWWhZRHCmdNq74CJYfaozM2gZCZqEDs',
    qa3: 'BdWWhZRHCmdNq74CJYfaozM2gZCZqEDs',
    prod: 'XhnbsDYmBensf2vqVXAfv0uD7SJBA27u',
    local: 'wU5VdAI4fv7jgsaAOJDREixc3MOaUs1R',
  })

  const callACSPrehydration = (cookie: string): void => {
    const url = `${window.location.origin}/acs/v3/customers/prehydration/${cookie}`

    fetch(url, {
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'api-key': apiKey,
        Accept: 'application/json, text/javascript, */*; q=0.01',
        ApplicationId: 'ALLYUSBOLB',
        ApplicationName: 'AOB',
        ApplicationVersion: '1.0',
        'patron-id': 'olbWeb',
        spname: 'common-api',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.data?.access_token}`,
      },
    })
  }

  useEffect(() => {
    if (sessionStatus.status === 'Authenticated') {
      /**
       * SSO URL redirection logic moved from useRedirectPath to here due to a race condition
       * caused by setting session state on the host triggering changes while SSO request
       * was in flight.
       */
      const { SSOREQUIRED, SSOURL } = sessionStatus.data

      if (SSOREQUIRED && SSOURL) {
        log.debug({
          message: `User authenticated and being redirected to SSOURL: ${SSOURL}`,
        })

        window.location.href = SSOURL
      } else {
        setSession({
          data: sessionStatus.data,
          status: 'Authenticated',
          destroy: logout,
        })
      }
    }
  }, [logout, setSession, sessionStatus])

  useEffect(() => {
    // NOTE: for future development, contingent on changes described in this thread https://ally-bank.slack.com/archives/C047EQDB8BG/p1675957323533959
    // after user authentication, a `set-cookie` response header will be set with altIdsToken
    const cookie = getCookieValue('hydrationLobs')
    if (cookie && isAccountsPrehydrationEnabled) {
      callACSPrehydration(cookie)
    }
  }, [])

  const faqLOB = featureFlags.variation(FeatureFlag.AosFaqLob, 'aos')
  useEffect(() => {
    const selectedCategory = faqLOB === 'spog' ? 'Security' : 'Login'
    utils.setHelpModalState({
      status: 'Closed',
      selectedCategory,
    })
  }, [])

  return <AllyLoginRouter defaultAuthRoute="/dashboard" />
}

/**
 * Checks that the user doesn't have an existing session (coming from the host
 * app). If they don't the <LoginForm /> is displayed. Otherwise the user is
 * redirected to the dashboard since they already have a session.
 */
export const Login: React.FC<LoginProps> = ({ className }) => {
  const { session } = useHostServices()

  useEffect(() => {
    if (session.status === 'Unauthenticated') {
      // This ensures that every time the login screen mounts, we clear profileVerificationStatus and storedcif.
      // There's unwanted behavior that happens when userA logs out while the sessionStorage value is set to `'PROMPT'`...
      // then userB logs in. Without this removal, userB would get prompted for AV. We can't rely on adding this to `logout`
      // since there are other ways the session could be invalidated. Therefore, the reset has to happen when userB logs in.
      window.sessionStorage.removeItem('profileVerificationStatus')
      window.sessionStorage.removeItem('storedcif')
    }
  }, [])

  return (
    <div className={className}>
      <LoginForm setSession={session.setSession} />
    </div>
  )
}

export default (): ReactElement => {
  /**
   * we need to hide AV and Login when all steps are done to avoid unwanted redirects to dashboard..
   * another solution would be to not have <Login /> be a "catch-all" route... but that
   * would involve ally-login-remote having to know the route layout of ally-login. It would be easier
   * if all login routes belonged to the same sub-route e.g. `/login/XYZ`, but unfortunately that's not the case.
   *
   * TODO: if ally-login ever gets more officially integrated into this remote and inherits its routes, clean this up.
   */

  const options: any = {}

  return (
    <AllyLoginProvider
      rehydrates={false}
      hostServices={useHostServices()}
      {...options}
    >
      <MaxAppWidth mt="sm">
        <Switch
          location={{
            ...window.location,
            state: window.history.state?.state || window.history.state,
          }}
        >
          <Route>
            <Login />
          </Route>
        </Switch>
      </MaxAppWidth>
    </AllyLoginProvider>
  )
}
